import { cn, FormInput, FormPrice, FormSelect, FormTextarea, RHF } from "@devhop/ui";
import {
  Building2Icon,
  ClipboardListIcon,
  DatabaseIcon,
  MailIcon,
  MegaphoneIcon,
  PhoneIcon,
  TagsIcon,
  UserCogIcon,
  UserIcon,
  UsersIcon,
  WalletIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import type { OutsourceCategoryDTO, StaffDTO } from "../../domain/contracts";
import {
  OutsourceCategoryBadge,
  OutsourcePayeeTypeBadge,
  formatOutsourceMoney,
} from "./outsource-table-cells";

export const AD_HOC_OUTSOURCE_VALUE = "__ad_hoc__";

export function toMasterSelectValue(outsourceId: string | null | undefined) {
  return outsourceId?.trim() ? outsourceId : AD_HOC_OUTSOURCE_VALUE;
}

export function fromMasterSelectValue(value: string | undefined) {
  return value && value !== AD_HOC_OUTSOURCE_VALUE ? value : undefined;
}

const STAFF_LOOKUP = `${config.apiUrl}/staff/lookup`;

type StaffLookupItem = {
  id: string;
  name: string;
  defaultRoleLabel?: string | null;
};

export function applyStaffToOutsourceForm(
  setValue: RHF.UseFormSetValue<Record<string, unknown>>,
  staff: StaffDTO,
  mode: "master" | "event",
) {
  setValue("displayName", staff.displayName, { shouldDirty: true });
  setValue("phone", staff.phone ?? "", { shouldDirty: true });
  setValue("bankName", staff.bankName ?? "", { shouldDirty: true });
  setValue("bankAccount", staff.bankAccount ?? "", { shouldDirty: true });
  setValue("bankAccountName", staff.accountName ?? "", { shouldDirty: true });
  setValue("payeeType", "individual", { shouldDirty: true });

  if (mode === "master") {
    setValue("notes", staff.notes ?? "", { shouldDirty: true });
  } else if (staff.defaultRoleLabel) {
    setValue("roleLabel", staff.defaultRoleLabel, { shouldDirty: true });
  }
}

export function OutsourceStaffPickField({ name }: { name: string }) {
  return (
    <OutsourceFormSection
      title="ດຶງຈາກ Staff"
      description="ເລືອກ Staff ທີ່ມີຢູ່ແລ້ວ — ຈະເຕີມຊື່/ເບີ/ບັນຊີອັດຕະໂນມັດ (ແກ້ໄຂກ່ອນບັນທຶກได้)"
      icon={UserCogIcon}
    >
      <FormInfiniteCombobox<StaffLookupItem>
        name={name}
        label="Staff"
        placeholder="ຄົ້ນຫາ Staff..."
        className="w-full min-w-0 max-w-full"
        clearable
        queryKey={["performance", "staff", "lookup", "outsource-prefill"]}
        queryFn={({ search, pageParam }) =>
          fetchLookupForInfinite<StaffLookupItem>(STAFF_LOOKUP, {
            search,
            pageParam,
          })
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem<StaffLookupItem>(STAFF_LOOKUP, id)
        }
        getLabel={(item) =>
          item.defaultRoleLabel
            ? `${item.name} · ${item.defaultRoleLabel}`
            : item.name
        }
        getValue={(item) => item.id}
      />
    </OutsourceFormSection>
  );
}

export function OutsourceFormSection({
  title,
  description,
  icon: Icon,
  children,
  className,
}: {
  title: string;
  description?: string;
  icon?: typeof UserIcon;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "min-w-0 max-w-full space-y-4 rounded-xl border bg-muted/20 p-4 sm:p-5",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        {Icon ? (
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background text-primary">
            <Icon className="size-4" />
          </span>
        ) : null}
        <div className="min-w-0">
          <h3 className="font-semibold text-sm uppercase tracking-wide">
            {title}
          </h3>
          {description ? (
            <p className="mt-0.5 text-muted-foreground text-xs leading-relaxed">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      <div className="min-w-0 max-w-full space-y-4 [&>*]:min-w-0 [&>*]:max-w-full">
        {children}
      </div>
    </section>
  );
}

export function OutsourcePayeeTypePicker({
  name,
  disabled,
}: {
  name: string;
  disabled?: boolean;
}) {
  const { control } = RHF.useFormContext();

  const options = [
    {
      value: "individual" as const,
      label: "ບຸກຄົນ",
      description: "KOL / influencer / freelancer",
      icon: UserIcon,
    },
    {
      value: "team" as const,
      label: "ທີມ / ບໍລິສັດ",
      description: "Production house / agency",
      icon: UsersIcon,
    },
  ];

  return (
    <RHF.Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className="grid min-w-0 max-w-full gap-2 sm:grid-cols-2">
          {options.map((option) => {
            const selected = field.value === option.value;
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                disabled={disabled}
                onClick={() => field.onChange(option.value)}
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                  selected
                    ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                    : "border-border bg-background hover:bg-muted/50",
                  disabled && "cursor-not-allowed opacity-60",
                )}
              >
                <span
                  className={cn(
                    "flex size-8 shrink-0 items-center justify-center rounded-md border",
                    selected
                      ? "border-primary/30 bg-primary/10 text-primary"
                      : "border-border bg-muted/40 text-muted-foreground",
                  )}
                >
                  <Icon className="size-4" />
                </span>
                <span className="min-w-0">
                  <span className="block font-medium text-sm">{option.label}</span>
                  <span className="mt-0.5 block text-muted-foreground text-xs">
                    {option.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    />
  );
}

export function OutsourceCategoryField({
  name,
  categories,
  disabled,
}: {
  name: string;
  categories: OutsourceCategoryDTO[];
  disabled?: boolean;
}) {
  const categoryId = RHF.useWatch({ name });
  const selected = categories.find((c) => c.id === categoryId);
  const options = categories
    .filter((c) => c.isActive)
    .map((c) => ({ value: c.id, label: `${c.nameLo} (${c.slug})` }));

  return (
    <div className="min-w-0 max-w-full space-y-2">
      <FormSelect
        name={name}
        label="ຫມວດ Outsource"
        options={options}
        requiredMark
        disabled={disabled}
      />
      {selected ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed bg-background/80 px-3 py-2">
          <OutsourceCategoryBadge
            name={selected.nameLo}
            slug={selected.slug}
          />
          {selected.defaultPayeeType ? (
            <OutsourcePayeeTypeBadge payeeType={selected.defaultPayeeType} />
          ) : null}
          <span className="text-muted-foreground text-xs">
            ເລືອກຫມວດແລ້ວຈະເຕີມ payeeType อັດຕະໂນມັດ
          </span>
        </div>
      ) : null}
    </div>
  );
}

export function OutsourceMasterPickField({
  name,
  masterOptions,
}: {
  name: string;
  masterOptions: Array<{ value: string; label: string }>;
}) {
  return (
    <OutsourceFormSection
      title="ດຶງຈາກ Master"
      description="ເລືອກ KOL / vendor ທີ່ບັນທຶກໄວ້ແລ້ວ — ຫຼື ad-hoc ປ້ອນໃໝ່"
      icon={DatabaseIcon}
    >
      <FormSelect
        name={name}
        label="Master Outsource"
        options={[
          { value: AD_HOC_OUTSOURCE_VALUE, label: "— ad-hoc (ປ້ອນໃໝ່) —" },
          ...masterOptions,
        ]}
      />
    </OutsourceFormSection>
  );
}

export function OutsourceIdentityFields({
  payeeType,
}: {
  payeeType: "individual" | "team";
}) {
  return (
    <OutsourceFormSection
      title="ຂໍ້ມູນຜູ້ຮັບ"
      description={
        payeeType === "team"
          ? "ຊື່ທີມ/ບໍລິສັດ + ຜູ້ຕິດຕໍ່ (ຖ້າມີ)"
          : "ຊື່ KOL / freelancer ທີ່ຈະຈ່າຍ AP"
      }
      icon={Building2Icon}
    >
      <FormInput name="displayName" label="ຊື່ຜູ້ຮັບ" requiredMark />
      {payeeType === "team" ? (
        <FormInput name="contactName" label="ຜູ້ຕິດຕໍ່ (ທາງເລືອກ)" />
      ) : null}
    </OutsourceFormSection>
  );
}

export function OutsourceEventDealFields() {
  return (
    <OutsourceFormSection
      title="ສັນຍາ / ຍອດ AP"
      description="ບັນທຶກຕົ້ນທຸນຈິງຕໍ່ແຖວ — AR ຢູ່ໃນເອກະສານ"
      icon={ClipboardListIcon}
    >
      <FormInput name="roleLabel" label="ບົດບາດ / Package (ທາງເລືອກ)" />
      <FormTextarea
        name="deliverableSummary"
        label="ຜົນງານ / Deliverable"
        rows={3}
        placeholder="ຕົວຢ່າງ: 3 TikTok posts + 1 live 30 ນາທີ"
      />
      <FormPrice name="agreedAmount" label="ຍອດຕົກລົງ (ກີບ)" requiredMark />
    </OutsourceFormSection>
  );
}

export function OutsourceContactFields() {
  return (
    <OutsourceFormSection
      title="ຕິດຕໍ່"
      description="ໃຊ້ຕິດຕໍ່ກ່ອນຈ່າຍ — snapshot ລົງໃບເບີກຈ່າຍ"
      icon={PhoneIcon}
    >
      <div className="grid min-w-0 max-w-full gap-3 sm:grid-cols-2">
        <FormInput name="phone" label="ເບີໂທ" />
        <FormInput name="email" label="ອີເມວ" />
      </div>
    </OutsourceFormSection>
  );
}

export function OutsourceBankFields() {
  return (
    <OutsourceFormSection
      title="ບັນຊີຮັບເງິນ"
      description="Snapshot ລົງໃບເບີກຈ່າຍ outsource — ກະລຸນາກອກໃຫ້ຄົບກ່ອນສ້າງ payout"
      icon={WalletIcon}
    >
      <FormInput name="bankName" label="ທະນາຄານ" />
      <div className="grid min-w-0 max-w-full gap-3 sm:grid-cols-2">
        <FormInput name="bankAccount" label="ເລກບັນຊີ" />
        <FormInput name="bankAccountName" label="ຊື່ບັນຊີ" />
      </div>
    </OutsourceFormSection>
  );
}

export function OutsourceFormPreview({
  displayName,
  payeeType,
  categoryName,
  categorySlug,
  agreedAmount,
  mode,
}: {
  displayName?: string;
  payeeType?: "individual" | "team";
  categoryName?: string | null;
  categorySlug?: string | null;
  agreedAmount?: number;
  mode: "master" | "event";
}) {
  if (!displayName?.trim()) return null;

  return (
    <div className="min-w-0 max-w-full rounded-xl border border-primary/20 bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-background text-primary">
          <MegaphoneIcon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
            {mode === "event" ? "ຕົວຢ່າງແຖວ AP" : "ຕົວຢ່າງ master"}
          </p>
          <p className="mt-1 font-semibold text-base leading-snug">{displayName}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {categoryName || categorySlug ? (
              <OutsourceCategoryBadge
                name={categoryName}
                slug={categorySlug}
              />
            ) : null}
            {payeeType ? <OutsourcePayeeTypeBadge payeeType={payeeType} /> : null}
          </div>
          {mode === "event" && agreedAmount != null && agreedAmount > 0 ? (
            <p className="mt-2 font-bold text-sm tabular-nums">
              {formatOutsourceMoney(agreedAmount)}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function OutsourceCategoryTypeSection({
  categories,
  showPayeePicker = true,
}: {
  categories: OutsourceCategoryDTO[];
  showPayeePicker?: boolean;
}) {
  return (
    <OutsourceFormSection
      title="ປະເພດງານ"
      description="ເລືອກຫມວດ KOL / media / vendor — ແຍກຈາກ Event category"
      icon={TagsIcon}
    >
      <OutsourceCategoryField name="categoryId" categories={categories} />
      {showPayeePicker ? (
        <div className="space-y-2">
          <p className="font-medium text-sm">ປະເພດຜູ້ຮັບ</p>
          <OutsourcePayeeTypePicker name="payeeType" />
        </div>
      ) : null}
    </OutsourceFormSection>
  );
}

export function OutsourceNotesField() {
  return (
    <OutsourceFormSection
      title="ຫມາຍເຫດ"
      description="ບັນທຶກຂໍ້ມູນເພີ່ມສຳລັບ master — ບໍ່ສະແດງໃນ QT/INV"
      icon={MailIcon}
    >
      <FormTextarea name="notes" label="ຫມາຍເຫດ" rows={3} />
    </OutsourceFormSection>
  );
}
